import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../../../../../../../api/api";

/** Chips shown at a time — the API returns ~20, we page through them 4 by 4. */
export const OBJECTIVES_PER_PAGE = 4;

/** Values are sent verbatim as `difficulty` on both story websockets. */
export const STORY_DIFFICULTY = {
  EASY: "Easy",
  MEDIUM: "Medium",
  HARD: "Hard",
};

export const STORY_DIFFICULTY_OPTIONS = [
  { label: "Easy", value: STORY_DIFFICULTY.EASY },
  { label: "Medium", value: STORY_DIFFICULTY.MEDIUM },
  { label: "Hard", value: STORY_DIFFICULTY.HARD },
];

const LEARNING_OBJECTIVES_URL = "ai/api/lower-school/learning-objectives/";

/** sessionStorage key for the in-progress Story Setup — survives a page refresh. */
export const STORY_SETUP_STORAGE_KEY = "lesson_storySetup";

/**
 * Identifies which lesson a stored draft belongs to. After a save the context
 * swaps the lesson for `{ Id, data }`, so `Id` must resolve to the same key.
 */
export const getLessonStorageKey = (lesson) => {
  const id = [lesson?.lesson_id, lesson?.id, lesson?.Id].find(
    (value) => value != null && value !== ""
  );
  return String(id ?? "new");
};

/** Fingerprint of the setup saved on the lesson — tells a stale draft apart. */
const getSavedSetupSignature = (lesson) =>
  JSON.stringify([
    lesson?.difficulty ?? null,
    lesson?.learning_objective ?? null,
    lesson?.learning_objective_description ?? null,
  ]);

/**
 * Draft left by a page refresh, or null. A draft only counts for the same
 * lesson and only while the lesson's saved setup is still what the draft was
 * based on — otherwise the API values are newer and the prefill must win.
 */
const readStoredStorySetup = (lessonKey, savedSetupSignature) => {
  try {
    const raw = sessionStorage.getItem(STORY_SETUP_STORAGE_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (
      draft?.lessonKey === lessonKey &&
      draft?.savedSetupSignature === savedSetupSignature
    ) {
      return draft;
    }
    sessionStorage.removeItem(STORY_SETUP_STORAGE_KEY);
    return null;
  } catch (error) {
    console.error("Failed to read stored story setup:", error);
    return null;
  }
};

const toStringArray = (value) =>
  Array.isArray(value) ? value.filter((item) => typeof item === "string") : [];

const toText = (value) => (typeof value === "string" ? value : "");

/** Maps a saved difficulty onto our option values, ignoring case. */
const normalizeDifficulty = (value) =>
  Object.values(STORY_DIFFICULTY).find(
    (option) => option.toLowerCase() === String(value || "").trim().toLowerCase()
  ) || null;

/**
 * Saved objectives normally come back as an array, but tolerate a JSON-encoded
 * array or a single plain string too.
 */
const normalizeObjectives = (value) => {
  let list = value;
  if (typeof value === "string") {
    try {
      list = JSON.parse(value);
    } catch {
      list = [value];
    }
  }
  if (!Array.isArray(list)) return [];
  return [
    ...new Set(
      list
        .filter((item) => typeof item === "string")
        .map((item) => item.trim())
        .filter(Boolean)
    ),
  ];
};

/**
 * Owns the Story Setup form state (difficulty, learning objectives, description)
 * so the lesson websocket payload can pick it up from the context.
 *
 * Objectives come from `/ai/api/lower-school/learning-objectives/` ~20 at a time;
 * "Refresh" walks the fetched batch 4 chips at a time and only re-hits the API
 * once the batch is exhausted, passing everything already shown as
 * `prev_objectives` so the backend returns a fresh set.
 *
 * That API also returns a `description`, which is NOT shown to the teacher — it
 * is context for `/ws/learning_objective_description/`, whose streamed answer is
 * what fills the textbox when "Generate with AI" is clicked. The textbox's own
 * contents go back to that socket as `user_query`, so a re-generation can be
 * steered by whatever the teacher wrote (or edited) in there.
 */
const useStorySetup = ({ lessonName, courseId, token, savedLesson }) => {
  const lessonKey = getLessonStorageKey(savedLesson);
  /** Draft saved before a page refresh — read once, seeds the state below. */
  const [storedDraft] = useState(() =>
    readStoredStorySetup(lessonKey, getSavedSetupSignature(savedLesson))
  );
  /** Saved-lesson setup the current values started from (see readStoredStorySetup). */
  const savedSetupSignatureRef = useRef(
    storedDraft?.savedSetupSignature ?? getSavedSetupSignature(savedLesson)
  );
  /**
   * Only teacher edits are persisted — an untouched form must never leave a
   * draft behind that would block the API prefill on the next load.
   */
  const isDirtyRef = useRef(Boolean(storedDraft));

  const [storyDifficulty, setStoryDifficultyState] = useState(
    () => normalizeDifficulty(storedDraft?.storyDifficulty) || STORY_DIFFICULTY.EASY
  );
  /** Current batch returned by the API. */
  const [objectivePool, setObjectivePool] = useState([]);
  /** Which slice of `objectivePool` is on screen. */
  const [objectivePage, setObjectivePage] = useState(0);
  /** Every objective ever shown for this lesson — sent back as `prev_objectives`. */
  const [seenObjectives, setSeenObjectives] = useState(() =>
    toStringArray(storedDraft?.seenObjectives)
  );
  const [selectedObjectives, setSelectedObjectivesState] = useState(() =>
    toStringArray(storedDraft?.selectedObjectives)
  );
  /** Objectives typed in by the teacher — pinned ahead of the API chips. */
  const [customObjectives, setCustomObjectives] = useState(() =>
    toStringArray(storedDraft?.customObjectives)
  );
  /** Text of the box under the chips — filled by the description websocket. */
  const [storyDescription, setStoryDescriptionState] = useState(() =>
    toText(storedDraft?.storyDescription)
  );
  /**
   * Last description the websocket produced, verbatim — untouched by the
   * teacher's edits. Sent back as `previous_description` on the next generate.
   */
  const [generatedStoryDescription, setGeneratedStoryDescriptionState] =
    useState(() => toText(storedDraft?.generatedStoryDescription));
  /** `description` from the objectives API: websocket context, never displayed. */
  const [objectiveApiDescription, setObjectiveApiDescription] = useState(() =>
    toText(storedDraft?.objectiveApiDescription)
  );
  const [objectivesLoading, setObjectivesLoading] = useState(false);
  const [objectivesError, setObjectivesError] = useState("");
  /** Guards against overlapping fetches from rapid Refresh clicks. */
  const fetchInFlightRef = useRef(false);

  /** Clears the refresh draft so the next load starts from the saved lesson. */
  const clearStoredDraft = useCallback(() => {
    isDirtyRef.current = false;
    try {
      sessionStorage.removeItem(STORY_SETUP_STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear stored story setup:", error);
    }
  }, []);

  /**
   * Prefill from a saved story lesson (difficulty / objectives / description).
   * Runs once per lesson so a later re-save — which hands back the same values
   * — never overwrites edits the teacher has made since. A draft restored on
   * mount was based on these same saved values, so it counts as prefilled.
   */
  const prefilledLessonRef = useRef(storedDraft ? lessonKey : null);
  useEffect(() => {
    if (!savedLesson) return;
    const {
      difficulty,
      learning_objective,
      learning_objective_description,
    } = savedLesson;
    const hasSavedSetup =
      difficulty != null ||
      learning_objective != null ||
      learning_objective_description != null;
    if (!hasSavedSetup) return;

    if (prefilledLessonRef.current === lessonKey) return;
    prefilledLessonRef.current = lessonKey;

    // The API values now take over — any older draft no longer applies.
    savedSetupSignatureRef.current = getSavedSetupSignature(savedLesson);
    clearStoredDraft();

    const savedDifficulty = normalizeDifficulty(difficulty);
    if (savedDifficulty) setStoryDifficultyState(savedDifficulty);

    const savedObjectives = normalizeObjectives(learning_objective);
    if (savedObjectives.length) {
      setSelectedObjectivesState(savedObjectives);
      // Treated as custom so they survive a lesson rename and stay pinned
      // even when the API's suggestions don't include them.
      setCustomObjectives(savedObjectives);
      setSeenObjectives((prev) => [...new Set([...prev, ...savedObjectives])]);
    }

    if (typeof learning_objective_description === "string") {
      setStoryDescriptionState(learning_objective_description);
      // Lets the next "Generate with AI" see what was saved as its previous output.
      setGeneratedStoryDescriptionState(learning_objective_description);
    }
  }, [savedLesson]);

  // Setters handed to the UI — every call is a teacher edit worth persisting.
  const setStoryDifficulty = useCallback((value) => {
    isDirtyRef.current = true;
    setStoryDifficultyState(value);
  }, []);
  const setSelectedObjectives = useCallback((value) => {
    isDirtyRef.current = true;
    setSelectedObjectivesState(value);
  }, []);
  const setStoryDescription = useCallback((value) => {
    isDirtyRef.current = true;
    setStoryDescriptionState(value);
  }, []);
  const setGeneratedStoryDescription = useCallback((value) => {
    isDirtyRef.current = true;
    setGeneratedStoryDescriptionState(value);
  }, []);

  // Persist the teacher's edits so a page refresh doesn't wipe them. The
  // objective pool itself isn't stored — StorySetup refetches it on mount.
  useEffect(() => {
    if (!isDirtyRef.current) return;
    try {
      sessionStorage.setItem(
        STORY_SETUP_STORAGE_KEY,
        JSON.stringify({
          lessonKey,
          savedSetupSignature: savedSetupSignatureRef.current,
          storyDifficulty,
          selectedObjectives,
          customObjectives,
          seenObjectives,
          storyDescription,
          generatedStoryDescription,
          objectiveApiDescription,
        })
      );
    } catch (error) {
      // Quota / private mode — the form still works, it just won't survive a refresh.
      console.error("Failed to persist story setup:", error);
    }
  }, [
    lessonKey,
    storyDifficulty,
    selectedObjectives,
    customObjectives,
    seenObjectives,
    storyDescription,
    generatedStoryDescription,
    objectiveApiDescription,
  ]);

  const visibleObjectives = useMemo(
    () =>
      objectivePool.slice(
        objectivePage * OBJECTIVES_PER_PAGE,
        objectivePage * OBJECTIVES_PER_PAGE + OBJECTIVES_PER_PAGE
      ),
    [objectivePool, objectivePage]
  );

  /** True when the on-screen slice is the last one in the fetched batch. */
  const isLastObjectivePage =
    (objectivePage + 1) * OBJECTIVES_PER_PAGE >= objectivePool.length;

  const resetStorySetup = useCallback(() => {
    clearStoredDraft();
    setStoryDifficultyState(STORY_DIFFICULTY.EASY);
    setObjectivePool([]);
    setObjectivePage(0);
    setSeenObjectives([]);
    setSelectedObjectivesState([]);
    setCustomObjectives([]);
    setStoryDescriptionState("");
    setGeneratedStoryDescriptionState("");
    setObjectiveApiDescription("");
    setObjectivesError("");
  }, [clearStoredDraft]);

  /**
   * Objectives are generated for a specific lesson name — drop the batch when
   * the teacher renames the lesson so we never send stale objectives.
   * The description is left alone: it may have been edited by hand.
   */
  const lastLessonNameRef = useRef((lessonName || "").trim());
  useEffect(() => {
    const nextLessonName = (lessonName || "").trim();
    if (lastLessonNameRef.current === nextLessonName) return;
    lastLessonNameRef.current = nextLessonName;
    setObjectivePool([]);
    setObjectivePage(0);
    setSeenObjectives([]);
    // Teacher-typed objectives aren't tied to the lesson name — keep them selected.
    setSelectedObjectivesState((prev) =>
      prev.filter((item) => customObjectives.includes(item))
    );
    setObjectivesError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonName]);

  const toggleObjective = useCallback((objective) => {
    setSelectedObjectives((prev) =>
      prev.includes(objective)
        ? prev.filter((item) => item !== objective)
        : [...prev, objective]
    );
  }, []);

  /**
   * Adds teacher-typed objectives as chips and selects them. Comma-separated
   * text ("Caring, Sharing") becomes one objective per part, so each is sent
   * as its own entry in `learning_objective`.
   * @returns {boolean} false when there is nothing to add
   */
  const addCustomObjective = useCallback((text) => {
    const objectives = [
      ...new Set(
        (text || "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
      ),
    ];
    if (!objectives.length) return false;
    const appendNew = (prev) => [
      ...prev,
      ...objectives.filter((item) => !prev.includes(item)),
    ];
    setCustomObjectives(appendNew);
    setSelectedObjectives(appendNew);
    return true;
  }, []);

  /** Deselects an objective; a teacher-typed one is dropped entirely. */
  const removeObjective = useCallback((objective) => {
    setSelectedObjectives((prev) => prev.filter((item) => item !== objective));
    setCustomObjectives((prev) => prev.filter((item) => item !== objective));
  }, []);

  /**
   * Fetches a new batch of objectives.
   * @param {boolean} allowEmptyLessonName let the call through without a lesson name
   *   (used by the first-mount fetch, which runs before the teacher has typed one)
   * @returns {Promise<string[]>} the fetched objectives (empty array on failure)
   */
  const fetchObjectives = useCallback(
    async ({ allowEmptyLessonName = false } = {}) => {
      const lesson_name = (lessonName || "").trim();
      if (!lesson_name && !allowEmptyLessonName) {
        setObjectivesError("Please enter the lesson name first");
        return [];
      }
      if (fetchInFlightRef.current) return [];

      fetchInFlightRef.current = true;
      setObjectivesLoading(true);
      setObjectivesError("");

      try {
        const response = await api.post(
          LEARNING_OBJECTIVES_URL,
          {
            lesson_name,
            prev_objectives: seenObjectives,
            course_id: courseId ?? null,
          },
          { headers: { Authorization: `Token ${token}` } }
        );

        const data = response?.data?.data || {};
        const objectives = Array.isArray(data.learning_objectives)
          ? data.learning_objectives.filter(Boolean)
          : [];

        if (!objectives.length) {
          setObjectivesError(
            lesson_name
              ? "No learning objectives found."
              : "Please enter the lesson name first"
          );
          return [];
        }

        // Drop anything already shown so Refresh never repeats a chip.
        const seen = new Set(seenObjectives);
        const freshObjectives = objectives.filter((item) => !seen.has(item));
        const nextPool = freshObjectives.length ? freshObjectives : objectives;

        setObjectivePool(nextPool);
        setObjectivePage(0);
        setSeenObjectives((prev) => [...new Set([...prev, ...nextPool])]);

        // Kept aside as websocket context — deliberately not shown in the textbox.
        if (data.description) setObjectiveApiDescription(data.description);

        return nextPool;
      } catch (error) {
        console.error("Failed to fetch learning objectives:", error);
        // The no-name first-mount fetch fails with a generic backend message — point the teacher at the real fix.
        setObjectivesError(
          !lesson_name
            ? "Please enter lesson name first"
            : error?.response?.data?.message ||
                "Could not load learning objectives. Please try again."
        );
        return [];
      } finally {
        fetchInFlightRef.current = false;
        setObjectivesLoading(false);
      }
    },
    [lessonName, courseId, seenObjectives, token]
  );

  /**
   * Refresh: show the next 4 from the current batch, or pull a new batch when
   * the fetched objectives are used up.
   */
  const refreshObjectives = useCallback(async () => {
    if (objectivesLoading) return;
    // Paging through the first-mount batch skips fetchObjectives' check, so guard here too.
    if (!(lessonName || "").trim()) {
      setObjectivesError("Please enter the lesson name first");
      return;
    }
    if (!objectivePool.length || isLastObjectivePage) {
      await fetchObjectives();
      return;
    }
    setObjectivePage((prev) => prev + 1);
  }, [
    fetchObjectives,
    isLastObjectivePage,
    lessonName,
    objectivePool.length,
    objectivesLoading,
  ]);

  return {
    storyDifficulty,
    setStoryDifficulty,
    visibleObjectives,
    selectedObjectives,
    setSelectedObjectives,
    toggleObjective,
    customObjectives,
    addCustomObjective,
    removeObjective,
    storyDescription,
    setStoryDescription,
    generatedStoryDescription,
    setGeneratedStoryDescription,
    objectivesLoading,
    objectivesError,
    objectivePool,
    objectiveApiDescription,
    fetchObjectives,
    refreshObjectives,
    resetStorySetup,
  };
};

export default useStorySetup;
